import { owlSessionAt } from "../../supabase/functions/_shared/owl-window.ts";
// Phase OWL-G1 — Owl Hour pre-reveal state machine.
//
// Resolves the screen state for the Owl Hour page using Accra time (UTC+0).
//
// State semantics for the two daily OWL refreshes:
//   PRE_SCAN  — After session close–11:00 Accra. Lock CTA inactive.
//   SCANNING  — 11:00–12:00, or later with no current-phase slip.
//               Agents assembling. Lock CTA inactive.
//   PUBLISHED — Current-phase slip exists AND not locked.
//               Real slip rendered. Lock CTA ACTIVE.
//   LOCKED    — Current-phase slip exists AND user has locked.
//               CTA replaced with "Slip locked ✓ · Results update as matches finish".

export type OwlScreenState = "PRE_SCAN" | "SCANNING" | "PUBLISHED" | "LOCKED";

// ─── WAT clock ────────────────────────────────────────────────

/** Legacy export name retained for callers; value is now Accra wall-clock. */
export function watHour(now: Date): number {
  return now.getUTCHours();
}

/** Legacy export name retained; returns the Accra minute 0–59. */
export function watMinute(now: Date): number {
  return now.getUTCMinutes();
}

/** Returns Accra minutes-since-midnight (0–1439). */
export function watMinutesSinceMidnight(now: Date): number {
  return watHour(now) * 60 + watMinute(now);
}

// ─── State resolver ───────────────────────────────────────────

const SCAN_START_WAT_MIN = 11 * 60;
const PUBLISH_WAT_MIN = 12 * 60;
const EVENING_REFRESH_WAT_MIN = 17 * 60;
function overnightActive(now:Date):boolean { return owlSessionAt(now).date!==now.toISOString().slice(0,10); }

export type OwlRefreshPhase = "noon" | "evening";

export function owlActiveRefreshPhase(now = new Date()): OwlRefreshPhase | null {
  const minutes = watMinutesSinceMidnight(now);
  if (overnightActive(now) || minutes >= EVENING_REFRESH_WAT_MIN) return "evening";
  if (minutes >= PUBLISH_WAT_MIN) return "noon";
  return null;
}

/** Versioned session ownership; new sessions remain active through the next 07:00 kickoff. */
export function owlSessionDate(now = new Date()): string { return owlSessionAt(now).date; }

export function resolveOwlScreenState(
  now: Date,
  slipExists: boolean,
  slipLocked: boolean,
): OwlScreenState {
  const watMin = watMinutesSinceMidnight(now);

  if (overnightActive(now)) {
    if (!slipExists) return "SCANNING";
    return slipLocked ? "LOCKED" : "PUBLISHED";
  }
  if (watMin < SCAN_START_WAT_MIN) return "PRE_SCAN";
  if (watMin < PUBLISH_WAT_MIN) return "SCANNING";
  // Accra time is at or beyond the noon publication boundary.
  if (!slipExists) return "SCANNING";
  return slipLocked ? "LOCKED" : "PUBLISHED";
}

// ─── CTA gating ───────────────────────────────────────────────

/** Lock CTA is only active in PUBLISHED. */
export function isLockCtaActive(state: OwlScreenState): boolean {
  return state === "PUBLISHED";
}

// ─── Copy ─────────────────────────────────────────────────────

export const OWL_STATE_COPY = {
  PRE_SCAN: "Today's slate begins loading at 11:00 Accra time",
  SCANNING: "Agents assembling tonight's Owl Hour",
  LOCKED: "Slip locked ✓ · Results update as matches finish",
} as const;

// ─── Per-tier edge thresholds (mirror server TIER_CONFIG in
//     supabase/functions/owl-hour-pipeline/index.ts). Used by the UI to
//     render honest-empty diagnostics when the pipeline returned null
//     for a tier (no fixture cleared the gate). Keep in sync with server.
export const OWL_TIER_EDGE_FLOOR: Record<string, { minEdge: number; minFairProb: number }> = {
  rollover:     { minEdge: 0.020, minFairProb: 0.50 },
  moderate:     { minEdge: 0.025, minFairProb: 0.45 },
  conservative: { minEdge: 0.030, minFairProb: 0.40 },
  aggressive:   { minEdge: 0.040, minFairProb: 0.35 },
  owl_1000:     { minEdge: 0.000, minFairProb: 0.40 },
};

/** True iff the Accra clock is past the publish boundary — used by
 *  OwlSignalCard to distinguish "still assembling" from "honest-empty". */
export function isPastPublishWindow(now: Date): boolean {
  const minutes = watMinutesSinceMidnight(now);
  return minutes >= PUBLISH_WAT_MIN || overnightActive(now);
}
